/**
 * 活动管理 — GET/POST/PATCH/DELETE /api/admin/activities
 */
import { useState } from 'react';
import { Button, DatePicker, Form, Input, Modal, Popconfirm, Switch, Table, Tag, message } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface ActivityRow {
  id: number;
  title: string;
  cover_url: string | null;
  body: string;
  start_time: string;
  end_time: string;
  is_published: boolean;
  create_time: string;
}

interface ActivityForm {
  title: string;
  cover_url?: string;
  body: string;
  start_time: Dayjs;
  end_time: Dayjs;
  is_published: boolean;
}

export function ActivityPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<ActivityRow | null>(null);
  const [createForm] = Form.useForm<ActivityForm>();
  const [editForm] = Form.useForm<ActivityForm>();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('platform.activity.create');
  const canEdit = hasNode('platform.activity.edit');
  const canDelete = hasNode('platform.activity.delete');

  const { data = [], isFetching } = useQuery({
    queryKey: ['admin', 'activities'],
    queryFn: () => api.get<ActivityRow[]>('/api/admin/activities'),
  });

  async function onCreate(values: ActivityForm) {
    try {
      await api.post('/api/admin/activities', {
        title: values.title,
        cover_url: values.cover_url || null,
        body: values.body,
        start_time: values.start_time.toISOString(),
        end_time: values.end_time.toISOString(),
        is_published: values.is_published,
      });
      message.success('活动已创建');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'activities'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: ActivityForm) {
    if (!editRow) return;
    try {
      await api.patch(`/api/admin/activities/${editRow.id}`, {
        title: values.title,
        cover_url: values.cover_url || null,
        body: values.body,
        start_time: values.start_time.toISOString(),
        end_time: values.end_time.toISOString(),
        is_published: values.is_published,
      });
      message.success('已更新');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'activities'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  async function onDelete(id: number) {
    try {
      await api.del(`/api/admin/activities/${id}`);
      message.success('已删除');
      qc.invalidateQueries({ queryKey: ['admin', 'activities'] });
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '删除失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: '封面',
      dataIndex: 'cover_url',
      width: 120,
      render: (v: string | null) =>
        v ? <img src={v} alt="" style={{ height: 40, maxWidth: 100, objectFit: 'cover' }} /> : '—',
    },
    { title: '标题', dataIndex: 'title', ellipsis: true },
    { title: '开始时间', dataIndex: 'start_time', width: 170 },
    { title: '结束时间', dataIndex: 'end_time', width: 170 },
    {
      title: '状态',
      dataIndex: 'is_published',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">已发布</Tag> : <Tag>草稿</Tag>),
    },
    {
      title: '操作',
      key: 'op',
      width: 150,
      render: (_: unknown, row: ActivityRow) => (
        <>
          {canEdit && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setEditRow(row);
                editForm.setFieldsValue({
                  title: row.title,
                  cover_url: row.cover_url ?? undefined,
                  body: row.body,
                  start_time: dayjs(row.start_time),
                  end_time: dayjs(row.end_time),
                  is_published: row.is_published,
                });
              }}
            >
              编辑
            </Button>
          )}
          {canDelete && (
            <Popconfirm title="确定删除？" onConfirm={() => onDelete(row.id)}>
              <Button type="link" size="small" danger>
                删除
              </Button>
            </Popconfirm>
          )}
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        {canCreate && (
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建活动
          </Button>
        )}
      </div>

      <Table<ActivityRow>
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={data}
        columns={columns}
        pagination={false}
      />

      <Modal
        open={createOpen}
        title="新建活动"
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ is_published: true }}
          onFinish={onCreate}
        >
          <Form.Item name="title" label="标题" rules={[{ required: true, max: 200 }]}>
            <Input />
          </Form.Item>
          <Form.Item name="cover_url" label="封面 URL"><Input /></Form.Item>
          <Form.Item name="body" label="正文" rules={[{ required: true }]}>
            <Input.TextArea rows={8} />
          </Form.Item>
          <Form.Item name="start_time" label="开始时间" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end_time" label="结束时间" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_published" label="立即发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>

      <Modal
        open={editRow !== null}
        title="编辑活动"
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        width={720}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item name="title" label="标题" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="cover_url" label="封面 URL"><Input /></Form.Item>
          <Form.Item name="body" label="正文" rules={[{ required: true }]}>
            <Input.TextArea rows={8} />
          </Form.Item>
          <Form.Item name="start_time" label="开始时间" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="end_time" label="结束时间" rules={[{ required: true }]}>
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_published" label="发布" valuePropName="checked"><Switch /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
